import {
  ApplyArtifactFileVersionRequestArtifactType,
  ApplyArtifactFileVersionRequestFileType,
  UpdateArtifactsRequestArtifactType,
  UploadArtifactFileParamsArtifactType,
  api,
} from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { confirmModal } from "@/component/ConfirmModal";
import {
  MediaEditorItem,
  MediaEditorModal,
} from "@/component/MediaEditorModal";
import { ORIGINAL_MUSIC_VOICE_ID } from "@/component/MediaEditorModal/component/VoiceSelector";
import { useToast } from "@/component/Toast/useToast";
import { t } from "@/i18n/utils";
import { toolStore } from "@/store/tool";
import { unseenVersionStore } from "@/store/unseenVersion";
import { voiceStore } from "@/store/voice";
import { Attachment } from "@/types/attachment";
import {
  AvatarAudioType,
  ImageModelViewInfo,
  MediaVersion,
  ModelViewInfo,
  RegenerateParams,
  VideoModelViewInfo,
} from "@/types/mediaArtifact";
import { createGeneratingVersionsFromTaskIds } from "@/util/artifact";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { uploadAttachments } from "@/util/uploadAttachments";
import { useRouter } from "@bprogress/next/app";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { createModelResolver } from "../utils/modelResolver";
import { useCreditCheck } from "./useCreditCheck";

interface UseMediaEditorModalParams {
  threadId?: string;
  threadTitle: string;
  isThreadOwner: boolean;
  canEdit: boolean;
  imageModelList: ImageModelViewInfo[];
  videoModelList: VideoModelViewInfo[];
  items: Record<MediaEditorModalType, MediaEditorItem[]>;
  onVersionsApplied: (
    type: MediaEditorModalType,
    index: number,
    versions: MediaVersion[]
  ) => void;
  onVersionSelected: (
    type: MediaEditorModalType,
    index: number,
    versionId: string
  ) => void;
  onMetadataOverride?: (
    type: MediaEditorModalType,
    index: number,
    overrides: Partial<MediaVersion>
  ) => void;
}

export enum MediaEditorModalType {
  ReferenceImage = "referenceImage",
  ImageStoryboard = "imageStoryboard",
  VideoStoryboard = "videoStoryboard",
}

interface DraftState {
  currentVersionId?: string;

  prompt?: string;
  dialogue?: string;
  voiceId?: string;
  videoClipStartTime?: number;
  model?: ModelViewInfo;
  attachmentList?: Attachment[];
}

export const useMediaEditorModal = ({
  threadId,
  threadTitle,
  isThreadOwner,
  canEdit,
  imageModelList,
  videoModelList,
  items,
  onVersionsApplied,
  onVersionSelected,
  onMetadataOverride,
}: UseMediaEditorModalParams) => {
  const router = useRouter();
  const toast = useToast();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [draftState, setDraftState] = useState<DraftState>({});
  const [mediaEditorModalType, setMediaEditorModalType] =
    useState<MediaEditorModalType>(MediaEditorModalType.ImageStoryboard);

  const resolveModel = useMemo(
    () => createModelResolver(imageModelList, videoModelList),
    [imageModelList, videoModelList]
  );

  const mediaEditorItems = useMemo(() => {
    return items[mediaEditorModalType] || [];
  }, [mediaEditorModalType, items]);

  const modalConfig = useMemo(() => {
    switch (mediaEditorModalType) {
      case MediaEditorModalType.ReferenceImage:
        return {
          title: "Reference",
          modelList: imageModelList,
          applyArtifactType:
            ApplyArtifactFileVersionRequestArtifactType.ValueReferenceImageArtifact,
          applyFileType: ApplyArtifactFileVersionRequestFileType.ReferenceImage,
          uploadArtifactType:
            UploadArtifactFileParamsArtifactType.ValueReferenceImageArtifact,
          uploadFileType: "reference_image",
        };
      case MediaEditorModalType.ImageStoryboard:
        return {
          title: "Shot",
          modelList: imageModelList,
          applyArtifactType:
            ApplyArtifactFileVersionRequestArtifactType.ValueShotsArtifact,
          applyFileType:
            ApplyArtifactFileVersionRequestFileType.StartFrameImage,
          uploadArtifactType:
            UploadArtifactFileParamsArtifactType.ValueShotsArtifact,
          uploadFileType: "start_frame_image",
        };
      case MediaEditorModalType.VideoStoryboard:
        return {
          title: "Shot",
          modelList: videoModelList,
          applyArtifactType:
            ApplyArtifactFileVersionRequestArtifactType.ValueShotsArtifact,
          applyFileType: ApplyArtifactFileVersionRequestFileType.VideoClip,
          uploadArtifactType:
            UploadArtifactFileParamsArtifactType.ValueShotsArtifact,
          uploadFileType: "video_clip",
        };
      default:
        return {
          title: "",
          modelList: [],
          applyArtifactType: null,
          applyFileType: null,
          uploadArtifactType: null,
          uploadFileType: "",
        };
    }
  }, [mediaEditorModalType, imageModelList, videoModelList]);

  const { checkCredits } = useCreditCheck();

  const debouncedPersist = useDebouncedCallback(
    async (
      startTime: number,
      item: MediaEditorItem,
      index: number,
      type: MediaEditorModalType
    ) => {
      // 1. Sync to local session (Update Grid/Timeline in background)
      onMetadataOverride?.(type, index, { videoClipStartTime: startTime });

      // 2. Sync to Backend
      if (threadId && item.dataId && item.current?.filePath) {
        try {
          await api.threads.updateThreadArtifacts(threadId, {
            artifact_type:
              UpdateArtifactsRequestArtifactType.ValueShotsArtifact,
            data: {
              id: item.dataId,
              video_clip_file_path: item.current.filePath,
              video_clip_start_time: startTime,
            },
          });
        } catch (error) {
          toast.error(t("error.unknownError"));
          console.error(
            "Failed to sync video clip start time to backend:",
            error
          );
        }
      }
    },
    500
  );

  const activeMediaItem = useMemo(() => {
    const item = mediaEditorItems[currentIndex];
    if (!item) return null;

    // 如果没有 draft 变化，直接返回原对象（避免不必要的重建）
    if (Object.keys(draftState).length === 0) {
      return item;
    }

    // Merge draftState into current item
    const newItem = { ...item };
    if (newItem.current) {
      // 如果 draft 中指定了版本 ID，先从版本列表中找到该版本作为基准
      const baseVersion = draftState.currentVersionId
        ? newItem.versions.find((v) => v.id === draftState.currentVersionId) ||
          newItem.current
        : newItem.current;

      const currentVersion = baseVersion;

      // Calculate final attachment list
      const finalAttachmentList =
        "attachmentList" in draftState
          ? draftState.attachmentList ?? []
          : currentVersion.attachmentList;

      // 只有在 draftState 中确实有不同于当前版本的值时，才应用变更
      newItem.current = {
        ...currentVersion,
        id: draftState.currentVersionId || currentVersion.id,
        prompt:
          "prompt" in draftState
            ? (draftState.prompt as string)
            : currentVersion.prompt,
        dialogue:
          "dialogue" in draftState
            ? (draftState.dialogue as string)
            : currentVersion.dialogue,
        voiceId:
          "voiceId" in draftState
            ? (draftState.voiceId as string)
            : currentVersion.voiceId,
        videoClipStartTime:
          "videoClipStartTime" in draftState
            ? (draftState.videoClipStartTime as number)
            : currentVersion.videoClipStartTime,
        model:
          "model" in draftState
            ? (draftState.model as ModelViewInfo | undefined)
            : typeof currentVersion.model === "string"
            ? resolveModel(currentVersion.model)
            : currentVersion.model,
        attachmentList: finalAttachmentList,
      };
    }

    return newItem;
  }, [mediaEditorItems, currentIndex, draftState, resolveModel]);

  const handleClose = () => {
    // If the current version is generating (transient), revert to the last stable version
    // so the user doesn't come back to a "stuck" generating state.
    if (activeMediaItem?.current?.status === "generating") {
      // 通过传入空字符串清除选中状态，从而回退到 currentVersion
      onVersionSelected(mediaEditorModalType, currentIndex, "");
    }

    debouncedPersist.flush();
    setModalShow(false);
  };

  const showEditorModal = (index: number, type: MediaEditorModalType) => {
    setCurrentIndex(index);
    setDraftState({});
    setModalShow(true);
    setMediaEditorModalType(type);

    logger.event(VidMuseEvent.MediaEditorModalOpened, {
      type,
      index,
    });
  };

  // Automatically mark as seen when a version finishes generating while being viewed
  useEffect(() => {
    if (
      modalShow &&
      activeMediaItem?.current?.id &&
      activeMediaItem.current.status === "success"
    ) {
      unseenVersionStore.markAsSeen(activeMediaItem.current.id);
    }
  }, [
    modalShow,
    activeMediaItem?.current?.id,
    activeMediaItem?.current?.status,
  ]);

  const onReferenceImageClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(index, MediaEditorModalType.ReferenceImage);
  };

  const onImageStoryboardClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(index, MediaEditorModalType.ImageStoryboard);
  };

  const onVideoStoryboardClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(index, MediaEditorModalType.VideoStoryboard);
  };

  const handleGenerate = async (params: RegenerateParams) => {
    if (!threadId) {
      console.error("threadId is required");
      return;
    }

    // 如果操作被禁用，直接返回
    if (!canEdit) {
      return;
    }

    // Check credits before generating
    const hasCredits = await checkCredits(
      params.model || "",
      params.count,
      params.mediaType,
      activeMediaItem?.current?.shotDuration
    );
    if (!hasCredits) {
      return;
    }

    setGenerating(true);

    try {
      // 先上传所有附件，获取 URLs
      const attachmentUrls = await uploadAttachments(
        params.attachmentList || []
      );

      const isExtractMusic =
        params.mediaType === "avatar" &&
        params.avatarAudioType === AvatarAudioType.ExtractMusic;

      // 调用对应的生成工具
      let result: { task_id_list: string[] };

      if (mediaEditorModalType === MediaEditorModalType.ImageStoryboard) {
        result = await toolStore.callGenerateImageForShotTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          attachmentUrls,
          shotIndex: currentIndex + 1,
        });
      } else if (
        mediaEditorModalType === MediaEditorModalType.VideoStoryboard
      ) {
        // 判断声音类型：通过 voiceStore 查找对应的声音配置
        const voiceOption =
          params.mediaType === "avatar"
            ? voiceStore.voices.find((v) => v.value === params.voiceId)
            : null;
        const isSystemVoice = voiceOption?.type === "system";

        result = await toolStore.callGenerateVideoForShotTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          dialogue:
            params.mediaType === "avatar" && !isExtractMusic
              ? params.dialogue || ""
              : "",
          attachmentUrls,
          shotIndex: currentIndex + 1,
          // 根据声音类型传递不同参数：系统声音传 voiceId，用户上传的声音传 voicePath
          voiceId:
            params.mediaType === "avatar" && isSystemVoice && !isExtractMusic
              ? params.voiceId || ""
              : "",
          voicePath:
            params.mediaType === "avatar" && !isSystemVoice && !isExtractMusic
              ? params.voiceId || ""
              : "",
          generate_type: params.mediaType === "avatar" ? "avatar" : "normal",
          avatar_audio_type: isExtractMusic
            ? AvatarAudioType.ExtractMusic
            : params.mediaType === "avatar"
            ? AvatarAudioType.Generate
            : AvatarAudioType.None,
        });
      } else if (mediaEditorModalType === MediaEditorModalType.ReferenceImage) {
        result = await toolStore.callReferenceImageTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          attachmentUrls,
          index: currentIndex + 1,
        });
      } else {
        return;
      }

      // Add to pending tasks
      result.task_id_list.forEach((taskId) => {
        unseenVersionStore.addPendingTaskId(taskId);
      });

      // 根据返回的 task_id_list 创建 generating 版本
      const generatingVersions = createGeneratingVersionsFromTaskIds(
        result.task_id_list,
        params.prompt || "",
        attachmentUrls,
        params.model || "",
        params.mediaType === "avatar" ? params.dialogue || "" : "",
        params.mediaType === "avatar"
          ? isExtractMusic
            ? ORIGINAL_MUSIC_VOICE_ID
            : params.voiceId || ""
          : ""
      ).map((version) => ({
        ...version,
        model: resolveModel(params.model),
        shotDuration: activeMediaItem?.current?.shotDuration,
        avatarAudioType: (isExtractMusic
          ? AvatarAudioType.ExtractMusic
          : params.mediaType === "avatar"
          ? AvatarAudioType.Generate
          : AvatarAudioType.None) as AvatarAudioType,
      }));

      onVersionsApplied(mediaEditorModalType, currentIndex, generatingVersions);

      logger.event(VidMuseEvent.MediaEditorModalGenerateBtn, {
        artifact_type: mediaEditorModalType,
        media_type: params.mediaType,
        thread_id: threadId,
        current_index: currentIndex,
        prompt: params.prompt,
        attachment_count: (params.attachmentList || []).length,
        model: params.model,
        count: params.count,
      });
    } catch (e) {
      const error = e as Error;
      const errorMessage = error.message
        ? error.message
        : t("error.generateFailed");

      if ((error as AxiosError).response?.status === 400) {
        const data = (error as AxiosError).response?.data as { code?: string };
        if (data?.code === "00400") {
          try {
            await confirmModal.confirm({
              title: t("modal.outOfCredits.title", "Out of credits"),
              content: t(
                "modal.outOfCredits.content",
                "You’ve used all available credits, upgrade or purchase a credit pack to keep creating."
              ),
              continueText: t("modal.outOfCredits.upgrade", "Upgrade"),
              backText: t("common.cancel", "Cancel"),
            });

            router.push("/pricing");
          } catch {
            // User cancelled
          }
        }
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRemix = () => {
    if (!canEdit) {
      return;
    }

    const current = activeMediaItem?.current;
    if (!current) {
      return;
    }

    const attachment: Attachment = {
      id: `remix-${current.id}`,
      type: "url",
      url: current.src,
      preview: current.previewSrc,
    };

    setDraftState((prev) => ({
      ...prev,
      currentVersionId: `remix-${current.id}`,
      prompt: current.prompt,
      dialogue: current.dialogue,
      voiceId: current.voiceId,
      model: current.model,
      attachmentList: [attachment],
    }));

    logger.event(VidMuseEvent.MediaEditorModalRemixBtn, {
      thread_id: threadId,
      media_type: activeMediaItem?.mediaType || "",
      current_index: currentIndex,
    });
  };

  const handleReplace = async (file: File) => {
    if (!threadId || !activeMediaItem) {
      console.error("threadId or activeMediaItem is required");
      return;
    }

    if (!modalConfig.uploadArtifactType || !modalConfig.uploadFileType) {
      console.error("uploadArtifactType or uploadFileType is required");
      return;
    }

    // 如果操作被禁用，直接返回
    if (!canEdit) {
      return;
    }

    try {
      const response = await api.threads.uploadArtifactFile(
        threadId,
        {
          artifact_type: modalConfig.uploadArtifactType,
          data: JSON.stringify({
            index: currentIndex,
            file_type: modalConfig.uploadFileType,
          }),
        },
        {
          file,
        }
      );

      // 如果上传成功并返回了 file_path，创建一个临时的成功版本
      if (response.data.file_path) {
        const src = convertFilePathToUrl(response.data.file_path);

        // 创建一个临时的成功版本
        const tempVersion: MediaVersion = {
          id: response.data.file_path,
          src: src,
          previewSrc: src,
          filePath: response.data.file_path,
          source: "upload",
          prompt: "",
          attachmentList: [],
          status: "success",
          createdTime: Date.now(),
          avatarAudioType: activeMediaItem?.current?.avatarAudioType,
        };

        // 获取现有的手动版本列表，追加新版本而不是覆盖
        const existingManualVersions =
          activeMediaItem.versions.filter(
            (v) => v.source === "upload" || v.source === "ref-video"
          ) || [];
        const updatedManualVersions = [...existingManualVersions, tempVersion];

        // 添加临时版本到手动版本列表中
        onVersionsApplied(
          mediaEditorModalType,
          currentIndex,
          updatedManualVersions
        );

        onVersionSelected(mediaEditorModalType, currentIndex, tempVersion.id);

        setDraftState((prev) => ({
          ...prev,
          currentVersionId: tempVersion.id,
        }));

        logger.event(VidMuseEvent.MediaEditorModalReplaceBtn, {
          thread_id: threadId,
          artifact_type: modalConfig.uploadArtifactType,
          media_type: activeMediaItem?.mediaType || "",
          current_index: currentIndex,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to replace artifact file";
      console.error(errorMessage);
      throw error;
    }
  };

  const handleModalIndexChange = (index: number) => {
    debouncedPersist.flush();
    setCurrentIndex(index);
    setDraftState({}); // Reset draft state when changing index
  };

  const handlePromptChange = useCallback((prompt: string) => {
    setDraftState((prev) => ({ ...prev, prompt }));
  }, []);

  const handleDialogueChange = useCallback((dialogue: string) => {
    setDraftState((prev) => ({ ...prev, dialogue }));
  }, []);

  const handleVoiceIdChange = useCallback((voiceId: string) => {
    setDraftState((prev) => ({ ...prev, voiceId }));
  }, []);

  const handleModelChange = useCallback((model: ModelViewInfo) => {
    setDraftState((prev) => ({ ...prev, model }));
  }, []);

  const handleSelectVersion = async (version: MediaVersion) => {
    if (!threadId) {
      console.error("threadId is required");
      return;
    }

    if (version.id === activeMediaItem?.current?.id) {
      return;
    }

    if (!modalConfig.applyArtifactType || !modalConfig.applyFileType) {
      console.error("applyArtifactType or applyFileType is required");
      return;
    }

    setDraftState({
      currentVersionId: version.id,
    });

    onVersionSelected(mediaEditorModalType, currentIndex, version.id);

    if (version.status === "success" && isThreadOwner) {
      try {
        await api.threads.applyArtifactFileVersion(threadId, {
          index: currentIndex,
          artifact_type: modalConfig.applyArtifactType,
          file_type: modalConfig.applyFileType,
          file_path: version.filePath,
        });
      } catch (error) {
        console.error("Failed to apply artifact file version:", error);
      }
    }

    logger.event(VidMuseEvent.MediaEditorModalSelectVersionBtn, {
      thread_id: threadId,
      artifact_type: modalConfig.applyArtifactType,
      media_type: activeMediaItem?.mediaType || "",
      current_index: currentIndex,
    });
  };

  const renderMediaEditorModal = () => {
    if (!activeMediaItem || !threadId) {
      return null;
    }

    return (
      <MediaEditorModal
        config={{
          threadId,
          threadTitle,
          title: modalConfig.title,
          canEdit,
          isOwner: isThreadOwner,
          modelList: modalConfig.modelList,
          mode: mediaEditorModalType,
        }}
        navigation={{
          index: currentIndex,
          totalCount: mediaEditorItems.length,
        }}
        data={{
          item: activeMediaItem,
        }}
        status={{
          open: modalShow,
          generating,
        }}
        actions={{
          onClose: handleClose,
          onUpdateDraft: (updates: Partial<MediaVersion>) => {
            if (updates.prompt !== undefined)
              handlePromptChange(updates.prompt);
            if (updates.dialogue !== undefined)
              handleDialogueChange(updates.dialogue);
            if (updates.voiceId !== undefined)
              handleVoiceIdChange(updates.voiceId);
            if (updates.videoClipStartTime !== undefined) {
              setDraftState((prev) => ({
                ...prev,
                videoClipStartTime: updates.videoClipStartTime,
              }));
              if (activeMediaItem) {
                debouncedPersist(
                  updates.videoClipStartTime,
                  activeMediaItem,
                  currentIndex,
                  mediaEditorModalType
                );
              }
            }
            if (updates.model !== undefined) handleModelChange(updates.model);
            if (updates.attachmentList !== undefined) {
              setDraftState((prev) => ({
                ...prev,
                attachmentList: updates.attachmentList,
              }));
            }
          },
          onGenerate: handleGenerate,
          onReplace: handleReplace,
          onIndexChange: handleModalIndexChange,
          onSelectVersion: handleSelectVersion,
          onRemix: handleRemix,
          onTypeChange: (newType: string) => {
            setMediaEditorModalType(newType as MediaEditorModalType);
          },
          onMarkDirty: () => {},
        }}
      >
        <MediaEditorModal.Preview isActive={modalShow} />
        <MediaEditorModal.Control />
      </MediaEditorModal>
    );
  };

  const handleAssetSelect = useCallback(
    (asset: { path: string }) => {
      const newAttachment: Attachment = {
        type: "url",
        id: asset.path,
        url: convertFilePathToUrl(asset.path),
        preview: convertFilePathToUrl(asset.path, { level: "medium" }),
      };

      setDraftState((prev) => {
        const currentList =
          "attachmentList" in prev
            ? prev.attachmentList ?? []
            : activeMediaItem?.current?.attachmentList || [];
        return {
          ...prev,
          attachmentList: [...currentList, newAttachment],
        };
      });
    },
    [activeMediaItem]
  );

  return {
    modalShow,
    onReferenceImageClick,
    onImageStoryboardClick,
    onVideoStoryboardClick,
    handleAssetSelect,
    renderMediaEditorModal,
  };
};
